import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Sheet from "@mui/joy/Sheet";
import DebounceInput from "../../components/debounceInput/DebounceInput";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/joy/IconButton";
import Divider from "@mui/joy/Divider";
import ConfirmDialog from "../../components/confirmDialog/ConfirmDialog";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import CircularProgress from "@mui/joy/CircularProgress";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import Dropdown from "@mui/joy/Dropdown";
import MenuButton from "@mui/joy/MenuButton";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import Menu from "@mui/joy/Menu";
import MenuItem from "@mui/joy/MenuItem";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ListDivider from "@mui/joy/ListDivider";
import {isProxyUnmapped, Proxy, ProxyDestination, ProxyDestinationDescription} from "../../models/proxy";
import {formatDateTime} from "../../common/dateTime";
import Chip from "@mui/joy/Chip";
import Tooltip from "@mui/joy/Tooltip";
import {deleteProxyApi, upsertProxyApi, resetFailedProxyApi, proxiesSearchApi, ProxiesSearchRequest} from "./proxiesApi";
import {NotificationService} from "../../common/notification";
import EditorDialog from "./EditorDialog";
import UnusedVendorDialog from "./UnusedVendorDialog";
import AddIcon from '@mui/icons-material/Add';
import Button from "@mui/joy/Button";
import Select from "@mui/joy/Select";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import Option from "@mui/joy/Option";
import {ProxyStatusIcon} from "./ProxyStatusIcon";
import {useNavigate} from "react-router-dom";

const unmappedHint = "Нет id слота Proxy-seller — автосмена IP не работает. Дождитесь сверки или впишите id вручную.";

function proxyRowStyle(proxy: Proxy): React.CSSProperties | undefined {
    if (proxy.failedAt !== undefined) {
        return {backgroundColor: 'red'};
    }
    if (isProxyUnmapped(proxy)) {
        return {backgroundColor: 'var(--joy-palette-warning-softBg, #fff8e6)'};
    }
    return undefined;
}

function ExternalIdMark({proxy}: {proxy: Proxy}) {
    if (!isProxyUnmapped(proxy)) {
        return <Typography level="body-xs">{proxy.externalId}</Typography>;
    }
    return (
        <Tooltip title={unmappedHint}>
            <Chip size="sm" color="warning" variant="soft">не сопоставлен</Chip>
        </Tooltip>
    );
}

export function ProxiesList() {
    const navigate = useNavigate();
    const [proxies, setProxies] = React.useState<Proxy[]>([]);
    const [page, setPage] = React.useState(1);
    const [count, setCount] = React.useState(30);
    const [total, setTotal] = React.useState(0);
    const [destinationFilter, setDestinationFilter] = React.useState<number>(-1);
    const [includeFailed, setIncludeFailed] = React.useState<boolean>(false);
    const [unmappedFilter, setUnmappedFilter] = React.useState<number>(-1);
    const [filterSearch, setFilterSearch] = React.useState('');
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [unusedOpen, setUnusedOpen] = React.useState(false);
    const [selectedProxy, setSelectedProxy] = React.useState<Proxy>({
        id: 0,
        proxyPassword: '',
        proxyHost: '',
        proxyPort: 0,
        proxyLogin: '',
        destination: ProxyDestination.General,
        connectionFailures: 0
    });
    const [loading, setLoading] = React.useState(false);
    const [selectedToProxy, setSelectedToProxy] = React.useState(0);
    const id = React.useRef(0);

    React.useEffect(() => {
        loadData().catch(console.error);
    }, [page, count, destinationFilter, includeFailed, unmappedFilter])

    const loadData = async () => {
        setLoading(true)
        try {
            const request: ProxiesSearchRequest = {
                page,
                count,
                destination: destinationFilter === -1 ? null : destinationFilter,
                includeFailed: includeFailed,
                unmapped: unmappedFilter === -1 ? null : unmappedFilter === 1
            };
            let result = await proxiesSearchApi(request);
            if (result.result && result.value) {
                setProxies(result.value.items)
                setTotal(result.value.total)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const remove = (value: number) => {
        id.current = value;
        setSelectedToProxy(0);
        setConfirmOpen(true)
    }

    const onDelete = async () => {
        setLoading(true)
        try {
            const res = await deleteProxyApi(id.current, selectedToProxy)
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return;
            }
            await loadData()
        } finally {
            setLoading(false)
            id.current = 0;
        }
    }

    const edit = (proxy: Proxy) => {
        setSelectedProxy(proxy);
        setEditorOpen(true);
    }

    const onSave = async (proxy: Proxy) => {
        setLoading(true)
        try {
            const res = await upsertProxyApi(proxy)
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return;
            }
            await loadData()
        } finally {
            setLoading(false)
            id.current = 0;
        }
    }

    const deleteProxyContent=():React.ReactNode=> {
        return <FormControl sx={{flexGrow: 1}}>
            <FormLabel>Переместить всех ботов на выбранный прокси</FormLabel>
            <Select
                size="sm"
                startDecorator={<DeviceHubIcon/>}
                defaultValue={-1}
                value={selectedToProxy}
                onChange={(_, value) => setSelectedToProxy(value ?? 0)}
            >
                <Option key={0} value={0}>Не перемещать</Option>
                {
                    proxies.filter(p => p.id !== id.current).map((p) =>
                        <Option key={p.id} value={p.id}>{p.proxyHost}:{p.proxyPort}</Option>)
                }
            </Select>
        </FormControl>;
    }

    const resetFailure = async (id: number) => {
        setLoading(true)
        try {
            let result = await resetFailedProxyApi(id);
            if (result.result) {
                NotificationService.alertSuccess("Ошибка прокси успешно сброшена.")
                await loadData();
            } else {
                NotificationService.alertError("Не удалось сбросить ошибку прокси. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            {editorOpen &&
                <EditorDialog proxy={selectedProxy}
                              onConfirm={onSave}
                              open={editorOpen}
                              setOpen={setEditorOpen}/>
            }
            {unusedOpen &&
                <UnusedVendorDialog open={unusedOpen} setOpen={setUnusedOpen}/>
            }
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: {xs: 2, md: 6},
                    pt: {
                        xs: 'calc(12px + var(--Header-height))',
                        sm: 'calc(12px + var(--Header-height))',
                        md: 3,
                    },
                    pb: {xs: 2, sm: 2, md: 3},
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    height: '100dvh',
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: {xs: 'column', sm: 'row'},
                        alignItems: {xs: 'start', sm: 'center'},
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Прокси
                    </Typography>
                </Box>
                <React.Fragment>
                    <Sheet
                        className="SearchAndFilters-mobile"
                        sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
                    >
                        <DebounceInput
                            size="sm"
                            placeholder="Поиск"
                            startDecorator={<SearchIcon/>}
                            sx={{flexGrow: 1}}
                            debounceTimeout={1000}
                            handleDebounce={(val) => setFilterSearch(val)}
                        />
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => setUnusedOpen(true)}
                        >
                            Свободные
                        </Button>
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => edit({
                                id: 0,
                                proxyPassword: '',
                                proxyHost: '',
                                proxyPort: 0,
                                proxyLogin: '',
                                destination: ProxyDestination.General,
                                connectionFailures: 0
                            })}
                        >
                            <AddIcon/>
                        </IconButton>
                    </Sheet>
                    <ConfirmDialog
                        title="Удалить?"
                        open={confirmOpen}
                        setOpen={setConfirmOpen}
                        onConfirm={onDelete}
                        content={deleteProxyContent()}
                    >
                        Вы уверены что хотите удалить прокси?
                    </ConfirmDialog>
                    <Box
                        className="SearchAndFilters-tabletUp"
                        sx={{
                            borderRadius: 'sm',
                            py: 2,
                            display: {xs: 'none', sm: 'flex'},
                            flexWrap: 'wrap',
                            gap: 1.5,
                            '& > *': {
                                minWidth: {xs: '120px', md: '160px'},
                            },
                        }}
                    >
                        <FormControl sx={{flex: 1}} size="sm">
                            <FormLabel>Поиск прокси</FormLabel>
                            <DebounceInput
                                size="sm"
                                placeholder="Поиск"
                                startDecorator={<SearchIcon/>}
                                debounceTimeout={1000}
                                handleDebounce={(val) => setFilterSearch(val)}
                            />
                        </FormControl>
                        <FormControl sx={{minWidth: 180}} size="sm">
                            <FormLabel>Назначение</FormLabel>
                            <Select
                                value={destinationFilter}
                                onChange={(_, v) => setDestinationFilter(v ?? -1)}
                            >
                                <Option value={-1}>Все</Option>
                                {
                                    Object.entries(ProxyDestinationDescription).map((entity) => <Option key={Number(entity[0])}
                                        value={Number(entity[0])}>{entity[1]}</Option>)
                                }
                            </Select>
                        </FormControl>
                        <FormControl size="sm">
                            <FormLabel>Показывать упавшие</FormLabel>
                            <Select value={includeFailed ? 1 : 0}
                                    onChange={(_, v) => setIncludeFailed((v ?? 0) === 1)}>
                                <Option value={0}>Нет</Option>
                                <Option value={1}>Да</Option>
                            </Select>
                        </FormControl>
                        <FormControl sx={{minWidth: 180}} size="sm">
                            <FormLabel>ExternalId</FormLabel>
                            <Select
                                value={unmappedFilter}
                                onChange={(_, v) => setUnmappedFilter(v ?? -1)}
                            >
                                <Option value={-1}>Все</Option>
                                <Option value={1}>Не сопоставлены</Option>
                                <Option value={0}>Сопоставлены</Option>
                            </Select>
                        </FormControl>
                        <FormControl size="sm" sx={{
                            justifyContent: 'end'
                        }}>
                            <Button
                                size="sm"
                                variant="outlined"
                                color="neutral"
                                onClick={() => setUnusedOpen(true)}
                            >
                                Свободные у вендора
                            </Button>
                        </FormControl>
                        <FormControl size="sm" sx={{
                            justifyContent: 'end'
                        }}>
                            <IconButton
                                size="sm"
                                variant="outlined"
                                color="neutral"
                                onClick={() => edit({
                                    id: 0,
                                    proxyPassword: '',
                                    proxyHost: '',
                                    proxyPort: 0,
                                    proxyLogin: '',
                                    destination: ProxyDestination.General,
                                    connectionFailures: 0
                                })}
                            >
                                <AddIcon/>
                            </IconButton>
                        </FormControl>
                    </Box>
                    {loading ?
                        <CircularProgress/> :
                        <>
                            <Sheet
                                className="OrderTableContainer"
                                variant="outlined"
                                sx={{
                                    display: {xs: 'none', sm: 'initial'},
                                    width: '100%',
                                    borderRadius: 'sm',
                                    flexShrink: 1,
                                    overflow: 'auto',
                                    minHeight: 0,
                                }}
                            >
                                <Table
                                    aria-labelledby="tableTitle"
                                    stickyHeader
                                    hoverRow
                                    sx={{
                                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                        '--Table-headerUnderlineThickness': '1px',
                                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                        '--TableCell-paddingY': '4px',
                                        '--TableCell-paddingX': '8px',
                                    }}
                                >
                                    <thead>
                                    <tr>
                                        <th style={{width: 140, padding: '12px 6px'}}>Id</th>
                                        <th style={{width: 140, padding: '12px 6px'}}>Хост</th>
                                        <th style={{width: 100, padding: '12px 6px'}}>Порт</th>
                                        <th style={{width: 140, padding: '12px 6px'}}>Логин</th>
                                        <th style={{width: 140, padding: '12px 6px'}}>Пароль</th>
                                        <th style={{width: 140, padding: '12px 6px'}}>Назначение</th>
                                        <th style={{width: 140, padding: '12px 6px'}}>ExternalId</th>
                                        <th style={{width: 160, padding: '12px 6px'}}>Изменён</th>
                                        <th style={{width: 140, padding: '12px 6px'}}>Ошибки</th>
                                        <th style={{width: 140, padding: '12px 6px'}}>Бот</th>
                                        <th style={{width: 140, padding: '12px 6px'}}></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {proxies.filter(p => filterSearch === '' || p.proxyHost.includes(filterSearch)).map((row) => (
                                        <tr key={row.id} style={proxyRowStyle(row)}>
                                            <td>
                                                <Typography level="body-xs">{row.id}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{row.proxyHost}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{row.proxyPort}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{row.proxyLogin}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{row.proxyPassword}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{ProxyDestinationDescription[row.destination]}</Typography>
                                            </td>
                                            <td>
                                                <ExternalIdMark proxy={row}/>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{formatDateTime(row.updated)}</Typography>
                                            </td>
                                            <td>
                                                <ProxyStatusIcon connectionFailures={row.connectionFailures}
                                                                 failedAt={row.failedAt}
                                                                 failureReason={row.failureReason}
                                                                 resetFailureAt={row.resetFailureAt}/>
                                            </td>
                                            <td>
                                            {
                                                row.linkedBotId
                                                ? <Link underline="hover"
                                                        onClick={() => navigate(`/app/bots/${row.linkedBotId}`)}
                                                        level="body-xs"
                                                        sx={{fontWeight: 600}}>
                                                        {row.linkedBotName}
                                                    </Link>
                                                :<></>
                                            }
                                            </td>
                                            <td>
                                                <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                                                    <Link level="body-sm" component="button"
                                                          onClick={() => edit(row)}>
                                                        Редактировать
                                                    </Link>
                                                    <Dropdown>
                                                        <MenuButton
                                                            slots={{root: IconButton}}
                                                            slotProps={{
                                                                root: {
                                                                    variant: 'plain',
                                                                    color: 'neutral',
                                                                    size: 'sm'
                                                                }
                                                            }}
                                                        >
                                                            <MoreHorizRoundedIcon/>
                                                        </MenuButton>
                                                        <Menu size="sm" sx={{minWidth: 140}}>
                                                            <MenuItem
                                                                onClick={() => edit(row)}>Редактировать</MenuItem>
                                                            <Divider/>
                                                            <MenuItem color="danger"
                                                                      onClick={() => remove(row.id)}>Удалить</MenuItem>
                                                            {row.failedAt === undefined ? <></>
                                                            :<>
                                                                <Divider/>
                                                                <MenuItem color="warning" onClick={() => resetFailure(row.id)}>
                                                                    <ListItemIcon sx={{ minWidth: 0 }}><CleaningServicesIcon fontSize="small" /></ListItemIcon>
                                                                    <ListItemText primary="Сбросить ошибку"
                                                                                  primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                                </MenuItem>
                                                            </>}
                                                        </Menu>
                                                    </Dropdown>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </Sheet>

                            <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                                {proxies.filter(p => filterSearch === '' || p.proxyHost.includes(filterSearch)).map((listItem) => (
                                    <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                        <ListItem
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'start',
                                            }}
                                            style={proxyRowStyle(listItem)}
                                        >
                                            <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                                <div>
                                                    <Typography gutterBottom sx={{fontWeight: 600}}>
                                                        {listItem.proxyHost}:{listItem.proxyPort}
                                                    </Typography>
                                                    <Typography level="body-xs">{listItem.proxyLogin}</Typography>
                                                    <Typography level="body-xs">{listItem.proxyPassword}</Typography>
                                                    <Typography level="body-xs">{ProxyDestinationDescription[listItem.destination]}</Typography>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, my: 0.5}}>
                                                        <Typography level="body-xs">ExternalId:</Typography>
                                                        <ExternalIdMark proxy={listItem}/>
                                                    </Box>
                                                    <Typography level="body-xs">Изменён: {formatDateTime(listItem.updated)}</Typography>
                                                    {
                                                        listItem.linkedBotId
                                                        ? <Link underline="hover"
                                                                onClick={() => navigate(`/app/bots/${listItem.linkedBotId}`)}
                                                                level="body-xs"
                                                                sx={{fontWeight: 600}}>
                                                                Бот: {listItem.linkedBotName}
                                                            </Link>
                                                        :<></>
                                                    }
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                        <Link level="body-sm" component="button"
                                                              onClick={() => edit(listItem)}>
                                                            Редактировать
                                                        </Link>
                                                        <Dropdown>
                                                            <MenuButton
                                                                slots={{root: IconButton}}
                                                                slotProps={{
                                                                    root: {
                                                                        variant: 'plain',
                                                                        color: 'neutral',
                                                                        size: 'sm'
                                                                    }
                                                                }}
                                                            >
                                                                <MoreHorizRoundedIcon/>
                                                            </MenuButton>
                                                            <Menu size="sm" sx={{minWidth: 140}}>
                                                                <MenuItem
                                                                    onClick={() => edit(listItem)}>Редактировать</MenuItem>
                                                                <Divider/>
                                                                <MenuItem color="danger"
                                                                          onClick={() => remove(listItem.id)}>Удалить</MenuItem>
                                                                {listItem.failedAt === undefined ? <></>
                                                                :<>
                                                                    <Divider/>
                                                                    <MenuItem color="warning" onClick={() => resetFailure(listItem.id)}>
                                                                        <ListItemIcon sx={{ minWidth: 0 }}><CleaningServicesIcon fontSize="small" /></ListItemIcon>
                                                                        <ListItemText primary="Сбросить ошибку"
                                                                                      primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                                    </MenuItem>
                                                                </>}
                                                            </Menu>
                                                        </Dropdown>
                                                    </Box>
                                                </div>
                                                <ProxyStatusIcon connectionFailures={listItem.connectionFailures}
                                                                failedAt={listItem.failedAt}
                                                                failureReason={listItem.failureReason}
                                                                resetFailureAt={listItem.resetFailureAt}/>
                                            </ListItemContent>
                                        </ListItem>
                                        <ListDivider/>
                                    </List>
                                ))}
                            </Box>
                            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
                                <Select size="sm" value={count} onChange={(_, v) => setCount(v ?? 30)} sx={{mr: 1}}>
                                    <Option value={10}>10</Option>
                                    <Option value={30}>30</Option>
                                    <Option value={50}>50</Option>
                                    <Option value={100}>100</Option>
                                </Select>
                                <Select size="sm" value={page} onChange={(_, v) => setPage(v ?? 1)}>
                                    {Array.from({length: Math.max(1, Math.ceil(total / count))}, (_, i) => i + 1).map(p => (
                                        <Option key={p} value={p}>{p}</Option>
                                    ))}
                                </Select>
                            </Box>
                        </>
                    }
                </React.Fragment>
            </Box>
        </Box>
    );
}
